import { useCallback, useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { mergeMedicalRecordValues, nullifyTextFieldsToEmptyString } from "@/lib/transcription/merge-medical-record-values";
import { formatMedicalRecordClipboardText } from "@/lib/utils/medical-record-clipboard";
import { combineDateAndTime, isValidTimeHHmm } from "@/lib/utils/next-dose";
import {
  type MedicalRecordFormInput,
  type MedicalRecordInput,
  medicalRecordSchema,
} from "@/lib/validations/medical-record";

const medicalRecordFormSchema = medicalRecordSchema.and(
  z.object({
    create_follow_up_appointment: z.boolean().optional(),
    follow_up_time: z.string().optional(),
  }),
);

export interface MedicalRecordFormHandle {
  isDirty: () => boolean;
  applyValues: (values: Partial<MedicalRecordInput>) => void;
  getValues: () => MedicalRecordFormInput;
  getClipboardText: () => string;
  reset: (values?: Partial<MedicalRecordFormInput>) => void;
  submit: () => void;
}

export interface UseMedicalRecordFormOptions {
  petId: string;
  appointmentId?: string;
  clinicId: string;
  defaultValues?: Partial<MedicalRecordFormInput>;
  onSubmit: (data: MedicalRecordFormInput) => Promise<void>;
  onDirtyChange?: (isDirty: boolean) => void;
  readOnly?: boolean;
}

export interface MedicalRecordFormController {
  form: UseFormReturn<MedicalRecordFormInput>;
  handleSubmit: (data: MedicalRecordFormInput) => Promise<void>;
  actions: MedicalRecordFormHandle;
}

export const applyMedicalRecordValues = (
  form: Pick<UseFormReturn<MedicalRecordFormInput>, "getValues" | "setValue">,
  values: Partial<MedicalRecordInput>,
): void => {
  // A IA retorna o schema inteiro com `null` explicito nos campos nao mencionados.
  // Sem isso, um `setValue` com null vira literalmente o texto "null" no input.
  const sanitizedValues = nullifyTextFieldsToEmptyString(values);
  // O valor corrente tambem precisa passar pelo saneamento: um registro salvo com
  // colunas null e reaberto para edicao carrega null no form, e o merge preserva
  // esse corrente quando o payload nao traz nada melhor.
  const rawCurrent = form.getValues();
  const current = nullifyTextFieldsToEmptyString(rawCurrent) as MedicalRecordFormInput;
  const merged = mergeMedicalRecordValues(current, sanitizedValues);
  const mergedAsRecord = merged as Record<string, unknown>;
  // A comparacao usa o valor cru: se o form ainda guarda null e o merge resolveu
  // "", isso precisa contar como mudanca para o setValue acontecer.
  const currentAsRecord = rawCurrent as Record<string, unknown>;
  const valueEntries = Object.entries(sanitizedValues) as Array<
    [keyof MedicalRecordInput, MedicalRecordInput[keyof MedicalRecordInput]]
  >;

  valueEntries.forEach(([key]) => {
    const nextValue = mergedAsRecord[key];
    if (Object.is(currentAsRecord[key], nextValue)) return;
    form.setValue(
      key,
      nextValue as MedicalRecordFormInput[keyof MedicalRecordFormInput],
      { shouldDirty: true, shouldValidate: false },
    );
  });
};

export const useMedicalRecordForm = ({
  petId,
  appointmentId,
  clinicId,
  defaultValues,
  onSubmit,
  onDirtyChange,
  readOnly,
}: UseMedicalRecordFormOptions): MedicalRecordFormController => {
  const { t } = useTranslation("medicalRecords");
  const baseDefaults = useMemo(
    (): MedicalRecordFormInput => ({
      pet_id: petId,
      appointment_id: appointmentId || "",
      clinic_id: clinicId,
      consultation_date: new Date(),
      chief_complaint: "",
      history: "",
      behavior: "",
      diet: "",
      environment: "",
      weight: undefined,
      temperature: undefined,
      heart_rate: undefined,
      respiratory_rate: undefined,
      mucous_membranes: "",
      capillary_refill_time: "",
      hydration_status: "",
      lymph_nodes: "",
      cardiac_auscultation: "",
      respiratory_auscultation: "",
      abdominal_palpation: "",
      locomotor_system: "",
      skin_coat: "",
      other_systems: "",
      presumptive_diagnosis: "",
      definitive_diagnosis: "",
      treatment: "",
      follow_up_date: undefined,
      follow_up_notes: "",
      create_follow_up_appointment: false,
      follow_up_time: "",
      register_death: false,
      death_date: undefined,
      death_cause: "",
      // `defaultValues` pode vir de um registro ja salvo no banco com colunas de
      // texto null (campos que a IA nao mencionou na consulta). Sem sanitizar,
      // esse null sobrescreveria o "" acima e apareceria como texto "null" na tela.
      ...nullifyTextFieldsToEmptyString(defaultValues ?? {}),
    }),
    [appointmentId, clinicId, defaultValues, petId],
  );

  const form = useForm<MedicalRecordFormInput>({
    resolver: zodResolver(medicalRecordFormSchema),
    defaultValues: baseDefaults,
  });
  const followUpDate = form.watch("follow_up_date");
  const createFollowUpAppointment = form.watch("create_follow_up_appointment");
  const { isDirty } = form.formState;

  useEffect(() => {
    if (form.formState.isDirty) return;
    form.reset(baseDefaults);
  }, [baseDefaults, form]);

  useEffect(() => {
    if (followUpDate) return;
    form.clearErrors(["create_follow_up_appointment", "follow_up_time"]);
    if (form.getValues("create_follow_up_appointment")) {
      form.setValue("create_follow_up_appointment", false, {
        shouldDirty: true,
        shouldTouch: true,
      });
    }
    if (form.getValues("follow_up_time")) {
      form.setValue("follow_up_time", "", {
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  }, [followUpDate, form]);

  useEffect(() => {
    if (createFollowUpAppointment || !form.getValues("follow_up_time")) return;
    form.clearErrors("follow_up_time");
    form.setValue("follow_up_time", "", {
      shouldDirty: true,
      shouldTouch: true,
    });
  }, [createFollowUpAppointment, form]);

  useEffect(() => {
    if (readOnly) return;
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange, readOnly]);

  const handleSubmit = useCallback(
    async (data: MedicalRecordFormInput): Promise<void> => {
      form.clearErrors(["create_follow_up_appointment", "follow_up_time"]);

      if (data.create_follow_up_appointment) {
        if (!data.follow_up_date) {
          form.setError("create_follow_up_appointment", {
            type: "manual",
            message: t("form.validation.followUpDateRequired"),
          });
          return;
        }

        if (!data.follow_up_time || !isValidTimeHHmm(data.follow_up_time)) {
          form.setError("follow_up_time", {
            type: "manual",
            message: t("form.validation.followUpTimeRequired"),
          });
          return;
        }

        const dateOnly =
          data.follow_up_date instanceof Date
            ? data.follow_up_date
            : new Date(data.follow_up_date);
        data.follow_up_date = combineDateAndTime(dateOnly, data.follow_up_time);
      }

      await onSubmit(data);
    },
    [form, onSubmit, t],
  );

  const actions = useMemo<MedicalRecordFormHandle>(
    () => ({
      isDirty: () => form.formState.isDirty,
      applyValues: (values) => {
        applyMedicalRecordValues(form, values);
      },
      getValues: () => form.getValues(),
      getClipboardText: () =>
        formatMedicalRecordClipboardText(form.getValues()),
      reset: (values) => {
        if (values) {
          form.reset({ ...form.getValues(), ...values });
          return;
        }
        form.reset(baseDefaults);
      },
      submit: () => {
        void form.handleSubmit(handleSubmit)();
      },
    }),
    [baseDefaults, form, handleSubmit],
  );

  return useMemo(
    () => ({ form, handleSubmit, actions }),
    [actions, form, handleSubmit],
  );
};
